"use client"

import {
  useCallback,
  useRef,
  type ChangeEvent,
  type InputHTMLAttributes,
} from "react"
import { AlertCircle, File, FolderUp, Upload } from "lucide-react"
import {
  useDropzone,
  type Accept,
  type FileRejection,
} from "react-dropzone"

import { Button } from "@/components/ui/button"
import {
  ALL_SUPPORTED_FILE_TYPES,
  MAX_FILE_SIZE,
} from "@/components/ai/knowledge-base/types"
import { cn } from "@/lib/utils"
import { getKnowledgeBaseUploadPath } from "@/lib/ai/knowledge-base/upload-selection"
import { useTranslations } from "next-intl"



type DocumentFileDropzoneProps = {
  files: readonly File[]
  onFilesSelected: (files: File[]) => void
  onRemoveFile: (file: File) => void
  fileError: string | null
  onFileError: (error: string | null) => void
  accept?: Accept
  multiple?: boolean
  maxFiles?: number
  supportedTypesLabel?: string
  disabled?: boolean
  allowFolderSelection?: boolean
}

export function DocumentFileDropzone({
  files,
  onFilesSelected,
  onRemoveFile,
  fileError,
  onFileError,
  accept = ALL_SUPPORTED_FILE_TYPES,
  multiple = false,
  maxFiles,
  supportedTypesLabel = "PDF, DOCX, TXT, MD, XLSX, XLS, CSV",
  disabled = false,
  allowFolderSelection = false,
}: DocumentFileDropzoneProps) {

  const tUi = useTranslations("Ai.AiKnowledgeBaseDocumentFileDropzone")

  const folderInputRef = useRef<HTMLInputElement>(null)
  const resolvedMaxFiles = maxFiles ?? (multiple ? 0 : 1)
  const onDrop = useCallback(
    (acceptedFiles: File[], rejectedFiles: FileRejection[]) => {
      const recoveredFiles = recoverUnlimitedFileCountRejections(
        rejectedFiles,
        multiple,
        resolvedMaxFiles,
      )
      const remainingRejections = rejectedFiles.filter(
        rejection => !recoveredFiles.includes(rejection.file),
      )
      const selectedFiles = [...acceptedFiles, ...recoveredFiles]

      if (remainingRejections.length > 0) {
        onFileError(getFileRejectionMessage(tUi,
          remainingRejections[0],
          supportedTypesLabel,
          resolvedMaxFiles,
        ))
      } else {
        onFileError(null)
      }

      if (selectedFiles.length > 0) onFilesSelected(selectedFiles)
    },
    [
      multiple,
      onFileError,
      onFilesSelected,
      resolvedMaxFiles,
      supportedTypesLabel,
      tUi,
    ],
  )
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept,
    maxSize: MAX_FILE_SIZE,
    multiple,
    maxFiles: resolvedMaxFiles,
    disabled,
  })

  const handleFolderSelection = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = Array.from(event.target.files ?? [])
    event.target.value = ""
    if (selectedFiles.length === 0) return

    const validFiles = selectedFiles.filter(
      file => file.size <= MAX_FILE_SIZE && isAcceptedFile(file, accept),
    )
    const rejectedCount = selectedFiles.length - validFiles.length
    onFileError(
      rejectedCount > 0
        ? `Skipped ${rejectedCount} unsupported or oversized file${rejectedCount === 1 ? "" : "s"}.`
        : null,
    )
    if (validFiles.length > 0) onFilesSelected(validFiles)
  }, [accept, onFileError, onFilesSelected])

  return (
    <div className="space-y-4">
      <div
        {...getRootProps()}
        className={cn(
          "cursor-pointer rounded-lg border-2 border-dashed border-gray-300 p-8 text-center transition-colors hover:border-gray-400",
          isDragActive && "border-blue-500 bg-blue-50",
          fileError && "border-red-300 bg-red-50",
          disabled && "cursor-not-allowed opacity-60",
        )}
      >
        <input {...getInputProps()} />
        {files.length > 0 ? (
          <div className="space-y-3">
            <File className="mx-auto h-10 w-10 text-blue-600" />
            <div className="max-h-56 space-y-2 overflow-y-auto pr-1">
              {files.map((file) => (
                <div
                  key={getFileKey(file)}
                  className="flex items-center justify-between gap-3 rounded-md border bg-white px-3 py-2 text-left"
                >
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium">
                      {getKnowledgeBaseUploadPath(file) || file.name}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {formatFileSize(tUi, file.size)}
                    </p>
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={(event) => {
                      event.stopPropagation()
                      onRemoveFile(file)
                    }}
                    disabled={disabled}
                  >
                    <span key={tUi("remove")}>{tUi("remove")}</span></Button>
                </div>
              ))}
            </div>
            {multiple ? (
              <p className="text-xs text-muted-foreground">
                <span key={tUi("dropOrClickToAddMoreDocuments")}>{tUi("dropOrClickToAddMoreDocuments")}</span></p>
            ) : null}
          </div>
        ) : (
          <div className="space-y-2">
            <Upload className="mx-auto h-12 w-12 text-gray-400" />
            <p key={isDragActive
                ? tUi("dropTheValue0Here", { value0: String(multiple ? "files" : "file") })
                : tUi("dragDropValue0HereOrClickToSelect", { value0: String(multiple ? "files" : "a file") })} className="text-gray-600">
              {isDragActive
                ? tUi("dropTheValue0Here", { value0: String(multiple ? "files" : "file") })
                : tUi("dragDropValue0HereOrClickToSelect", { value0: String(multiple ? "files" : "a file") })}
            </p>
            <p className="text-xs text-gray-500">
              <span><span key={tUi("supported")}>{tUi("supported")}</span></span> {supportedTypesLabel} <span><span key={tUi("max")}>{tUi("max")}</span></span> {MAX_FILE_SIZE / 1024 / 1024}<span><span key={tUi("mBEach")}>{tUi("mBEach")}</span></span>
            </p>
          </div>
        )}
      </div>

      {allowFolderSelection ? (
        <div className="flex items-center justify-center">
          <input
            ref={folderInputRef}
            type="file"
            multiple
            accept={getNativeAcceptValue(accept)}
            onChange={handleFolderSelection}
            disabled={disabled}
            className="hidden"
            {...({ webkitdirectory: "", directory: "" } as InputHTMLAttributes<HTMLInputElement>)}
          />
          <Button
            type="button"
            variant="outline"
            onClick={() => folderInputRef.current?.click()}
            disabled={disabled}
          >
            <FolderUp className="mr-2 h-4 w-4" />
            <span><span key={tUi("selectFolder")}>{tUi("selectFolder")}</span></span>
          </Button>
        </div>
      ) : null}

      {fileError ? (
        <div className="flex items-center gap-2 text-sm text-red-600">
          <AlertCircle className="h-4 w-4" />
          {fileError}
        </div>
      ) : null}
    </div>
  )
}

function getFileRejectionMessage(tUi: ReturnType<typeof useTranslations<"Ai.AiKnowledgeBaseDocumentFileDropzone">>,
  rejection: FileRejection,
  supportedTypesLabel: string,
  maxFiles: number,
): string {
  switch (rejection.errors[0]?.code) {
    case "file-too-large":
      return tUi("fileTooLargeMaximumSizeIsValue0MB", { value0: String(MAX_FILE_SIZE / 1024 / 1024) })
    case "file-invalid-type":
      return tUi("unsupportedFileTypeAllowedValue0", { value0: String(supportedTypesLabel) })
    case "too-many-files":
      return tUi("tooManyFilesSelectedMaximumForThisUpload", { value0: String(maxFiles) })
    default:
      return rejection.errors[0]?.message || tUi("invalidFile")
  }
}

export function recoverUnlimitedFileCountRejections(
  rejectedFiles: FileRejection[],
  multiple: boolean,
  maxFiles: number,
): File[] {
  if (!multiple || maxFiles > 0) return []

  return rejectedFiles
    .filter(rejection => (
      rejection.errors.length > 0
      && rejection.errors.every(error => error.code === "too-many-files")
    ))
    .map(rejection => rejection.file)
}

function getFileKey(file: File): string {
  return `${getKnowledgeBaseUploadPath(file)}:${file.name}:${file.size}:${file.lastModified}`
}

function isAcceptedFile(file: File, accept: Accept): boolean {
  if (file.type && Object.hasOwn(accept, file.type)) return true

  const lowerName = file.name.toLowerCase()
  return Object.values(accept)
    .flat()
    .some(extension => lowerName.endsWith(extension.toLowerCase()))
}

function getNativeAcceptValue(accept: Accept): string {
  return Object.entries(accept)
    .flatMap(([mimeType, extensions]) => [mimeType, ...extensions])
    .join(",")
}

function formatFileSize(tUi: ReturnType<typeof useTranslations<"Ai.AiKnowledgeBaseDocumentFileDropzone">>, bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return tUi("value0KB", { value0: String((bytes / 1024).toFixed(1)) })
  return tUi("value0MB", { value0: String((bytes / (1024 * 1024)).toFixed(1)) })
}
